<template>
  <button
    :aria-label="ariaLabel"
    type="button"
    class="zendesk-btn"
    @click="loadZendeskScript"
    v-if="isButtonShown"
  >
    <span class="zendesk-btn-icon">
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
        <path d="m14 18 4 4v-4h-4ZM7 6a5 5 0 0 0 0 10h10a5 5 0 0 0 0-10Z" />
      </svg>
    </span>
  </button>
</template>

<script lang="ts" setup>
import { ref } from 'vue'

interface Props {
  ariaLabel?: string
  widgetKey: string
  languageCode?: string /** Available language codes: https://support.zendesk.com/hc/en-us/articles/203761906 */
  trackEvent?: Function
}

const props = withDefaults(defineProps<Props>(), {
  ariaLabel: 'Help',
  languageCode: '',
  trackEvent: () => {},
})

const isButtonShown = ref(true)

const trackZendeskWidgetClicked = () => {
  if (!props.trackEvent) {
    return
  }

  const params = {
    page: window.location.href,
  }

  props.trackEvent('click_help_icon', { params })
}

function loadZendeskScript() {
  const widgetKey = props.widgetKey

  // track click event
  trackZendeskWidgetClicked()

  if (widgetKey.length === 0 || (window as any).zE) {
    return
  }

  // Static configuration needs to be declared before being used by the Zendesk widget
  ;(window as any).zESettings = {
    webWidget: {
      chat: {
        suppress: false,
        connectOnPageLoad: false,
      },
      contactForm: {
        suppress: false,
      },
      helpCenter: {
        suppress: false,
      },
      talk: {
        suppress: false,
      },
      answerBot: {
        suppress: false,
      },
    },
  }

  const src = `https://static.zdassets.com/ekr/snippet.js?key=${widgetKey}`
  const zendeskScript = document.createElement('script')
  zendeskScript.setAttribute('id', 'ze-snippet')
  zendeskScript.setAttribute('src', src)
  document.head.appendChild(zendeskScript)

  const onloadListener = () => {
    const zendeskFn = (window as any).zE

    if (props.languageCode) {
      zendeskFn('messenger:set', 'locale', props.languageCode)
    }

    zendeskFn('messenger:on', 'open', function () {
      isButtonShown.value = false
    })

    zendeskFn('messenger', 'open')
    zendeskScript.removeEventListener('load', onloadListener)
  }
  zendeskScript.addEventListener('load', onloadListener)
}
</script>

<style lang="scss" scoped>
// Styles are done in this manner because it has to be the same as zendesk's component
$btnSize: 64px;
$svgSize: 38px;
.zendesk-btn {
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #0f0f0f;
  fill: #fff;
  position: fixed;
  z-index: 999999;
  width: $btnSize;
  height: $btnSize;
  bottom: 16px;
  right: 16px;
  &-icon {
    display: flex;
    width: $svgSize;
    height: $svgSize;
  }
}
</style>
